import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useFormik } from 'formik';

import { errorToastMessage } from '../../../components/layout/toast-root/toast';
import { useLanguage } from '../../../context/LanguageContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useNotFound } from '../../../hooks/useNotFound';
import {
  useGetFilePaths,
  useGetFormulas,
} from '../../../redux/api/appConfigApi';
import {
  useGetExerciseOptions,
  useGetExerciseProperties,
} from '../../../redux/api/exerciseConfigAPI';
import {
  useCopyExercise,
  useGetExerciseById,
  usePostPutExercise,
} from '../../../redux/api/exercisesAPI';
import { useGetLanguages } from '../../../redux/api/translationsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import {
  ExercisePropertyType,
  ExerciseScope,
  FileVisibility,
} from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IAppFile } from '../../../types/interfaces/app-file';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { INewFile } from '../../../types/interfaces/new-file';
import { orderBy } from '../../../utils/collections/array-utils';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/files/firebase-storage-utils';
import { getExerciseValidation } from './FormikData';

export function useNewExercisePage({
  exerciseId,
  onExerciseCreate,
}: {
  exerciseId?: string;
  onExerciseCreate?: (exerciseId: string) => void;
}) {
  const [searchParams] = useSearchParams();
  const copyExerciseId = searchParams.get('copyExerciseId');
  const copiedExercise = !!copyExerciseId && !exerciseId;
  const [postPutPostExercise, { isLoading: isPostExerciseLoading }] =
    usePostPutExercise();
  const [copyExercise, { isLoading: isCopyExerciseLoading }] =
    useCopyExercise();
  const { navigate, back } = useNavigationStack();
  const { data: exerciseOptions } = useGetExerciseOptions();

  const {
    data: getExerciseResponse,
    error,
    isLoading: isGetExerciseLoading,
  } = useGetExerciseById(
    { id: copyExerciseId || exerciseId || '' },
    {
      skip: !exerciseId && !copyExerciseId,
    },
  );

  const exercise = useMemo(
    () => getExerciseResponse?.exercise || null,
    [getExerciseResponse],
  );

  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const { data: user } = useGetMyDetails();
  const { data: languages } = useGetLanguages();
  const { data: languagesData } = useGetLanguages();
  const { data: properties } = useGetExerciseProperties();
  const { data: formulasData } = useGetFormulas();
  const { data: filePaths } = useGetFilePaths();
  const [fileIdsToDelete, setFileIdsToDelete] = useState<string[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<INewFile[]>([]);
  const categories = useMemo(
    () =>
      properties?.filter((p) => p.type === ExercisePropertyType.category) || [],
    [properties],
  );
  const mainMuscleGroups = useMemo(
    () =>
      properties?.filter((p) => p.type === ExercisePropertyType.muscle) || [],
    [properties],
  );

  const equipments = useMemo(
    () =>
      properties?.filter((p) => p.type === ExercisePropertyType.equipment) ||
      [],
    [properties],
  );

  const developedSkillOptions = useMemo(
    () =>
      properties?.filter(
        (p) => p.type === ExercisePropertyType.developedSkill,
      ) || [],
    [properties],
  );

  const isNewExercise = !exerciseId && !copyExerciseId;

  const defaultExerciseProperties = useMemo((): {
    category: IExerciseProperty | null;
    mainMuscleGroup: IExerciseProperty | null;
    equipment: IExerciseProperty | null;
  } => {
    if (!properties) {
      return {
        category: null,
        mainMuscleGroup: null,
        equipment: null,
      };
    }
    return {
      category:
        properties.find(
          (p) =>
            p.type === ExercisePropertyType.category && p.isDefault === true,
        ) ?? null,
      mainMuscleGroup:
        properties.find(
          (p) => p.type === ExercisePropertyType.muscle && p.isDefault === true,
        ) ?? null,
      equipment:
        properties.find(
          (p) =>
            p.type === ExercisePropertyType.equipment && p.isDefault === true,
        ) ?? null,
    };
  }, [properties]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getExerciseValidation(t),
    initialValues: {
      title: exercise?.title || '',
      description: exercise?.description || '',
      category: isNewExercise
        ? defaultExerciseProperties.category
        : exercise?.category || null,
      mainMuscleGroup: isNewExercise
        ? defaultExerciseProperties.mainMuscleGroup
        : exercise?.mainMuscleGroup || null,
      equipment: isNewExercise
        ? defaultExerciseProperties.equipment
        : exercise?.equipment || null,
      additionalEquipments: exercise?.additionalEquipments || [],
      additionalMuscles: exercise?.additionalMuscles || [],
      developedSkills: exercise?.developedSkills || [],
      avatarFileId: exercise?.avatarFileId || null,
      splitBySide: exercise?.splitBySide || false,
      language: languages?.find(
        (l) => l.id === (exercise?.languageId || currentLanguage?.id),
      ),
      options: (exercise?.id
        ? exercise?.options
        : exerciseOptions?.filter((o) => o.default)
      )?.map((o) => o.id),
      exerciseLoadFormula:
        formulasData?.find(
          (f) => f.formula.id === exercise?.exerciseLoadFormulaId,
        )?.formula || null,
      exerciseLoadFormulaId: exercise?.exerciseLoadFormulaId || null,
    },
    onSubmit,
  });

  function isExerciseAvailable() {
    return error && 'status' in error && error?.status === 404;
  }

  useNotFound(isExerciseAvailable());

  async function onSubmit(update) {
    if (!isValidAvatar()) {
      formik.setFieldError(
        'avatarFileId',
        t('formikValidation.exerciseAvatarRequired'),
      );
      return;
    }

    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    const uploadedStoragePaths: string[] = [];
    let hasError = false;

    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        filesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.exercises,
            user.uid,
            user.id,
            FileVisibility.private,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);
          if (uploadedFile.previewStorageLink) {
            uploadedStoragePaths.push(uploadedFile.previewStorageLink);
          }
          return uploadedFile;
        }),
      );

      let mappedAvatarFileId = update.avatarFileId;
      if (typeof update.avatarFileId === 'number') {
        const fileIndex = filesToUpload.findIndex(
          (f) => f.id === update.avatarFileId,
        );
        if (fileIndex >= 0 && uploadedFiles[fileIndex]) {
          mappedAvatarFileId =
            uploadedFiles[fileIndex].id || update.avatarFileId;
        }
      }

      try {
        if (!copiedExercise) {
          const response = await postPutPostExercise({
            id: exerciseId || undefined,
            body: {
              exercise: {
                title: update.title,
                description: update.description,
                categoryId: update.category?.id || null,
                mainMuscleGroupId: update.mainMuscleGroup?.id || null,
                equipmentId: update.equipment?.id || null,
                additionalEquipmentIds:
                  update.additionalEquipments?.map((eq) => eq.id) || [],
                additionalMuscleIds:
                  update.additionalMuscles?.map((muscle) => muscle.id) || [],
                developedSkillIds:
                  update.developedSkills?.map((s) => s.id) || [],
                languageId: update.language.id,
                avatarFileId: mappedAvatarFileId,
                splitBySide: update.splitBySide,
                options: update.options.map((id) => ({ id })),
                exerciseLoadFormulaId: update.exerciseLoadFormulaId || null,
              },
              filesToUpload: uploadedFiles,
              fileIdsToDelete,
            },
          }).unwrap();

          if (onExerciseCreate) {
            onExerciseCreate(response?.id);
          } else {
            back();
          }
        } else {
          await copyExercise({
            id: copyExerciseId,
            body: {
              exercise: {
                title: update.title,
                description: update.description,
                categoryId: update.category?.id || null,
                mainMuscleGroupId: update.mainMuscleGroup?.id || null,
                equipmentId: update.equipment?.id || null,
                additionalEquipmentIds:
                  update.additionalEquipments?.map((eq) => eq.id) || [],
                additionalMuscleIds:
                  update.additionalMuscles?.map((muscle) => muscle.id) || [],
                developedSkillIds:
                  update.developedSkills?.map((s) => s.id) || [],
                languageId: update.language.id,
                avatarFileId: mappedAvatarFileId,
                options: update.options.map((id) => ({ id })),
                exerciseLoadFormulaId: update.exerciseLoadFormulaId || null,
              },
              filesToUpload: uploadedFiles,
              fileIdsToDelete,
            },
          }).unwrap();

          navigate(Paths.exercises.toList);
        }
      } catch (backendError) {
        hasError = true;
        console.error(
          'Error saving exercise after Firebase Storage upload:',
          backendError,
        );
        throw backendError;
      }
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading exercise files to Firebase Storage:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
      throw error;
    } finally {
      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
    }
  }

  function isValidAvatar() {
    const remainingFiles =
      exercise?.files?.filter((file) => !fileIdsToDelete.includes(file.id)) ??
      [];

    const hasFiles = filesToUpload.length > 0 || remainingFiles.length > 0;

    const isAvatarValid =
      filesToUpload.some(
        (file) => file.id === (formik.values.avatarFileId as any),
      ) ||
      remainingFiles.some((file) => file.id === formik.values.avatarFileId);

    return !hasFiles || isAvatarValid;
  }

  const availableFormulas = useMemo(() => {
    if (
      !formulasData ||
      !formik.values.options ||
      formik.values.options.length === 0
    ) {
      return [];
    }

    const selectedOptionIds = formik.values.options;
    const selectedOptions =
      exerciseOptions?.filter((opt) => selectedOptionIds.includes(opt.id)) ||
      [];

    const selectedNameKeys = selectedOptions.map((opt) => opt.nameKey);

    return formulasData.filter((formulaWithKeys) => {
      const formulaNameKeys = formulaWithKeys.nameKeys || [];
      return formulaNameKeys.every((key) => selectedNameKeys.includes(key));
    });
  }, [formulasData, formik.values.options, exerciseOptions]);

  function onChangeFormikOption(e: ChangeEvent<HTMLInputElement>, option) {
    const previousOptions = formik.values.options;
    if (e.target.checked) {
      formik.setFieldValue('options', [...previousOptions, option.id]);
    } else {
      formik.setFieldValue(
        'options',
        previousOptions.filter((o) => o !== option.id),
      );
    }
    formik.setFieldValue('exerciseLoadFormula', null);
    formik.setFieldValue('exerciseLoadFormulaId', null);
  }

  function getResultFiles(): Array<IAppFile | INewFile> {
    return [
      ...(exercise?.files.filter((f) => !fileIdsToDelete.includes(f.id)) || []),
      ...filesToUpload,
    ];
  }

  function onFilesToDeleteChanged(ids: string[]) {
    setFileIdsToDelete(ids);
    formik.setFieldError('avatarFileId', null);

    if (ids.includes(exercise?.avatarFileId)) {
      formik.setFieldValue('avatarFileId', null);
    }
  }

  function onFilesToUploadChanged(files: INewFile[]) {
    formik.setFieldError('avatarFileId', null);

    if (
      !(
        exercise?.files?.some((f) => f.id === exercise?.avatarFileId) ||
        files?.some((f) => f.id === (exercise?.avatarFileId as any))
      )
    ) {
      formik.setFieldValue('avatarFileId', null);
    }

    setFilesToUpload(files);
  }

  return {
    formik,
    exercise,
    fileIdsToDelete,
    filesToUpload,
    exerciseFiles: getResultFiles(),
    selectedFile,
    setSelectedFile,
    onFilesToDeleteChanged,
    onFilesToUploadChanged,
    onChangeFormikOption,
    exerciseOptions,
    isLoading:
      isPostExerciseLoading || isCopyExerciseLoading || isGetExerciseLoading,
    currentLanguage,
    languages: orderBy(languagesData, 'title'),
    categories,
    mainMuscleGroups,
    equipments,
    developedSkillOptions,
    copiedExercise,
    availableFormulas,
  };
}
